// src/components/TSOAuthDetector.tsx - Fixed with better error handling
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';

const TSOAuthDetector: React.FC = () => {
  const { refreshProfile, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasChecked, setHasChecked] = useState(false);
  
  useEffect(() => {
    // Only run this once
    if (hasChecked) return;
    
    const checkTSOToken = async () => {
      try {
        // Skip if already authenticated
        if (isAuthenticated) {
          setHasChecked(true);
          return;
        }
        
        // Check URL for token parameter
        const urlParams = new URLSearchParams(location.search);
        const urlToken = urlParams.get('auth_token');
                
        // Check localStorage
        let tsoToken = localStorage.getItem('sb-auth-token');
                
        // If URL has token, use it
        if (urlToken) {
          tsoToken = urlToken;
          // Save to localStorage for future use
          localStorage.setItem('sb-auth-token', urlToken);
          
          // Clean up URL
          if (window.history && window.history.replaceState) {
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
          }
        }
        
        if (!tsoToken) {
          setHasChecked(true);
          return;
        }
        
        // Parse the token
        let tokenData;
        try {
          tokenData = JSON.parse(tsoToken);
        } catch (parseErr) {
          console.error('Failed to parse token:', parseErr);
          // Clear invalid token
          localStorage.removeItem('sb-auth-token');
          setHasChecked(true);
          return;
        }
        
        // Set session with token
        if (tokenData?.access_token) {
          const { data, error } = await supabase.auth.setSession({
            access_token: tokenData.access_token,
            refresh_token: tokenData.refresh_token || ''
          });
          
          if (error) {
            console.error('Failed to set session');
            
            // If token is invalid/expired, clear it and don't retry
            if (error.message.includes('Invalid Refresh Token') || 
                error.message.includes('Already Used') ||
                error.message.includes('expired')) {
              localStorage.removeItem('sb-auth-token');
              console.log('Cleared invalid auth token');
            }
            
            setHasChecked(true);
            return;
          }
          
          // Success - refresh the profile
          if (data?.session) {
            await refreshProfile();
            
            // Navigate to dashboard if we're on the home page
            if (window.location.pathname === '/' || window.location.pathname === '') {
              navigate('/dashboard');
            }
          }
        }
      } catch (err: any) {
        console.error('Auth detection error');
        
        // Clear any problematic tokens
        if (err.message?.includes('refresh') || err.message?.includes('token')) {
          localStorage.removeItem('sb-auth-token');
        }
      } finally {
        setHasChecked(true);
      }
    };

    checkTSOToken();
  }, [navigate, refreshProfile, isAuthenticated, hasChecked, location]);

  // This component doesn't render anything visible
  return null;
};

export default TSOAuthDetector;